import { useRef } from "react";
import { Box } from "../Box";
import { Button } from "../Button";
import { Input } from "../Input";
import { NoteTextarea } from "../NoteTextarea";
import { SettingsCard as Card, SettingsHeading } from "../SettingsCard";
import { Text } from "../Text";
import { useToast } from "../Toast";

export type SkillForm = {
  name: string;
  description: string;
  instructions: string;
  enabled: boolean;
};

type Props = {
  form: SkillForm;
  disabled: boolean;
  onChange: (patch: Partial<SkillForm>) => void;
  onCommit: () => void;
  onUpload: (instructions: string) => void;
};

export function SettingsTab({
  form,
  disabled,
  onChange,
  onCommit,
  onUpload,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const text = await file.text();
    if (text.includes("\0")) {
      showToast({
        icon: "circle-off",
        message: "That file isn’t a text file. Upload a markdown (.md) file.",
        buttonLabel: "Dismiss",
        variant: "error",
      });
      return;
    }
    onUpload(text);
  };

  return (
    <Box gap={16}>
      <Card>
        <SettingsHeading>
          <Text variant="text-regular-md">Skill</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            Name your skill and tell the AI when to reach for it.
          </Text>
        </SettingsHeading>
        <Box>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md" color="var(--text-primary)">
              Name
            </Text>
          </Box>
          <Input
            value={form.name}
            disabled={disabled}
            placeholder="my-skill"
            style={{ width: "100%", minWidth: 0 }}
            onChange={(e) => onChange({ name: e.target.value })}
            onBlur={onCommit}
          />
        </Box>
        <Box>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md" color="var(--text-primary)">
              Description
            </Text>
          </Box>
          <NoteTextarea
            value={form.description}
            disabled={disabled}
            placeholder="When should the AI reach for this skill?"
            onChange={(e) => onChange({ description: e.target.value })}
            onBlur={onCommit}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              The AI reads this to decide when the skill applies.
            </Text>
          </Box>
        </Box>
      </Card>

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <SettingsHeading>
            <Text variant="text-regular-md">Instructions</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Markdown the AI loads into context when it uses this skill.
            </Text>
          </SettingsHeading>
          <Button
            variant="primary"
            icon="arrow-up"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload .md
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown,text/markdown"
            style={{ display: "none" }}
            onChange={handleFile}
          />
        </div>
        <NoteTextarea
          value={form.instructions}
          disabled={disabled}
          placeholder="# Instructions the AI loads when it uses this skill…"
          style={{
            minHeight: 176,
            fontFamily: "var(--font-mono)",
            fontWeight: 350,
            fontSize: "11px",
            lineHeight: "20px",
            letterSpacing: 0,
          }}
          onChange={(e) => onChange({ instructions: e.target.value })}
          onBlur={onCommit}
        />
      </Card>
    </Box>
  );
}
